import {
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { toast, ToastT } from 'sonner'

declare global {
  var WEBAUTHN_IS_AUTHENTICATING: boolean
}

// Chrome raises NotSupportedError "Error connecting to Web Authentication
// service" when it cannot reach the OS passkey provider, typically after a
// background browser update that is waiting for a relaunch.
export const getPasskeyErrorDescription = (
  error: unknown,
  fallback = 'Please try again',
) =>
  error instanceof Error && error.name === 'NotSupportedError'
    ? 'Quit and reopen your browser, then try again. If it keeps happening, contact support@splits.org.'
    : fallback

export const startPasskeyAuthentication = async (
  options: PublicKeyCredentialRequestOptionsJSON,

  {
    onError,
    toastId: toastIdArg,
    toastDescription,
  }: {
    onError: (onErrorArgs: {
      error: unknown
      isNotAllowedError: boolean
      isUnsupportedError?: boolean
    }) => void
    toastId?: string
    toastDescription?: string
  },
) => {
  if (
    !browserSupportsWebAuthn() ||
    !(await platformAuthenticatorIsAvailable())
  ) {
    onError({
      error: new Error(
        'Use a different browser or device to sign with a passkey',
      ),
      isNotAllowedError: false,
      isUnsupportedError: true,
    })
    return undefined
  }

  let attResp: AuthenticationResponseJSON | undefined = undefined
  let toastId: ToastT['id'] | undefined = undefined
  try {
    globalThis.WEBAUTHN_IS_AUTHENTICATING = true
    toastId = toast.loading('Waiting for signature...', {
      description: toastDescription,
      id: toastIdArg,
    })
    attResp = await startAuthentication({
      optionsJSON: options,
    })
  } catch (error) {
    const errorName = error instanceof Error ? error.name : 'UnknownError'
    const errorMessage = error instanceof Error ? error.message : String(error)
    const isNotAllowedError =
      errorMessage.includes('NotAllowedError') ||
      errorName === 'NotAllowedError'

    onError({
      error,
      isNotAllowedError,
    })
  } finally {
    globalThis.WEBAUTHN_IS_AUTHENTICATING = false
    if (toastId && !toastIdArg) {
      toast.dismiss(toastId)
    }
  }

  return attResp
}
